<template>
  <div class="map-side">
    <div class="map--widget" ref="map"></div>
  </div>
</template>

<script>
import {mapState} from "vuex";

export default {
  name: "search-map",

  data() {
    return {
      map: null,
      infoWindows: [],
      markers: [],
      markerConfig: {
        one: {
          url: '/static/img/map-pin.svg',
          size: [20, 20],
          origin: [0, 0],
          anchor: [10, 10],
          hover: {
            size: [30, 30],
            origin: [0, 0],
            anchor: [15, 15],
          }
        },
        multi: {
          url: '/static/img/building-light.svg',
          size: [24, 30],
          origin: [0, 0],
          anchor: [12, 30],
          hover: {
            size: [35, 50],
            origin: [0, 0],
            anchor: [17, 50],
          }
        }
      }
    }
  },
  computed: {
    rooms() {
      return this.$store.getters["search/geoRooms"];
    },
    ...mapState({
      styles: s => s.mapWidget.style,
      hovered: s => s.search.hovered
    }),
  },
  watch: {
    rooms(v) {
      this.init();
    },
    hovered(v) {
      const matchings = {
        smallOne: '/static/img/map-pin.svg',
        bigOne: '/static/img/map-big-pin.svg',
        smallMany: '/static/img/map-pin2.svg',
        bigMany: '/static/img/map-big-pin2.svg',
      };
      for (const marker of this.markers) {
        const hasReference = marker.references && marker.references.indexOf(v) !== -1;
        const iconConfig = marker.getIcon();
        const config = iconConfig.url === this.markerConfig.one.url ? this.markerConfig.one : this.markerConfig.multi;
        if (hasReference) {
          marker.setIcon({
            url: iconConfig.url,
            scaledSize: new google.maps.Size(...config.hover.size),
            anchor: new google.maps.Point(...config.hover.anchor),
          })
          return;
        }
        marker.setIcon({
          url: config.url,
          scaledSize: new google.maps.Size(...config.size),
          anchor: new google.maps.Point(...config.anchor),
        })
      }
    }
  },
  mounted() {
    this.init();
    window.addEventListener('app:click_room', this.handleRoomClick);
  },
  beforeDestroy() {
    window.removeEventListener('app:click_room', this.handleRoomClick);
  },
  methods: {
    handleRoomClick(e) {
      this.$store.commit('search/SET_SELECTED', e.detail);

    },
    init() {
      this.initMap();
      this.clearMarkers();
      for (const data of this.rooms) {
        this.addMarker(data.rooms, data.position.lat, data.position.lng);
      }
      this.centerMap();
    },
    centerMap() {
      if (this.rooms.length === 0) return;
      const positions = this.rooms.length === 0 ? [{lat: 45.764043, lng: 4.835659}] : this.rooms.map(r => r.position);
      const bounds = new google.maps.LatLngBounds();
      for (const pos of positions) {
        bounds.extend(pos);
      }
      this.map.fitBounds(bounds);
      const zoom = Math.min(15, this.map.getZoom());
      this.map.setZoom(zoom);
    },
    clearMarkers() {
      for (const m of this.markers) {
        m.setMap(null);
      }
      this.markers = [];
    },
    addMarker(rooms, lat, lng) {
      const config = this.markerConfig[rooms.length > 1 ? 'multi' : 'one'];
      let marker = new google.maps.Marker({
        references: rooms.map(r => r.id),
        map: this.map,
        position: {lat, lng},
        icon: {
          url: config.url,
          scaledSize: new google.maps.Size(...config.size),
          origin: new google.maps.Point(...config.origin),
          anchor: new google.maps.Point(...config.anchor),
        },
      });
      marker.rooms = rooms.map(r => r.id);

      const style = `<style>
        .s-popup-slider {
            position: relative;
        }
        .s-popup-slider-image {
          height: 110px;
          width: 100%;
          background-size: cover;
          background-position: center center;
          background-repeat: no-repeat;
        }
        .s-room-name {
            margin-top: .35rem;
            display: grid;
            grid-template-areas: 'a c' 'b c';
            align-items: center;
            grid-template-columns: auto min-content;
            grid-gap: .35rem;
            grid-row-gap: 0;
            font-family: Sofia Pro, sans-serif;
        }
        .s-popup-room {
            margin-bottom: 1rem;
        }
        .s-room-name :nth-child(1) {
            grid-area: a;
            font-weight: 600;
            font-size: 1.1em;
        }
        .s-room-name :nth-child(2) {grid-area: b;}
        .s-room-name :nth-child(3) {grid-area: c;}
        .s-room-name button {
            background: white;
            border: 1px solid #444;
            color: #444;
            font-family: Sofia Pro, sans-serif;
            font-size: .9em;
            text-transform: uppercase;
            cursor: pointer;
            padding: .25rem .35rem;
        }
        .s-room-name span {
            cursor: pointer;
        }
        .s-room-name button:hover {
            background: var(--orange);
            border-color: var(--orange);
            color: white;
        }
      </style>`;
      let infoWindow = new google.maps.InfoWindow({
        content: style + rooms.map(r => this.renderRoom(r)).join(''),
      });
      this.infoWindows.push(infoWindow);

      infoWindow.addListener('closeclick', () => {
        this.$store.commit('search/SET_MAP_SELECTED', []);
      })


      marker.addListener('click', () => {
        for (const info of this.infoWindows) {
          info.close();
        }
        // this.$store.commit('search/SET_MAP_SELECTED', marker.rooms);
        // window.dispatchEvent(new CustomEvent('app:scroll_top'))
        this.$parent.scrollToRoom(marker.rooms[0]);

        infoWindow.open(this.map, marker);
      });

      this.markers.push(marker);
    },
    renderRoom(room) {
      let picture = room.main_picture;
      if (!picture) {
        picture = room.pictures[0];
      }
      return `
            <div class="s-popup-room">
              <div class="s-popup-slider" onclick="window.dispatchEvent(new CustomEvent('app:click_room', {detail: ${room.id}}))" style="cursor: pointer">
                <div class="s-popup-slider-image" style="background-image: url('${picture.absolute_path}?w=500&h=250&crop-to-fit')"></div>
              </div>
              <div class="s-room-name">
                <span class="s-room-title"  onclick="window.dispatchEvent(new CustomEvent('app:click_room', {detail: ${room.id}}))">${room.text.short_name}</span>
                <span>${room.room_group.text.name}</span>
                <button onclick="window.dispatchEvent(new CustomEvent('app:click_room', {detail: ${room.id}}))">${this.$t('roomFinder.inputLook')}</button>
              </div>
            </div>
        `;
    },
    initMap() {
      if (this.map) return;
      const el = this.$refs.map;
      this.map = new google.maps.Map(el, {
        center: {lat: 45.756043, lng: 4.834659},
        streetViewControl: false,
        zoom: 15,
        disableDefaultUI: true,
        styles: this.styles,
      });
      this.map.addListener('click', () => {
        for (const info of this.infoWindows) {
          info.close();
        }
        this.$store.commit('search/SET_MAP_SELECTED', []);
      })
    }
  }
}
</script>

<style scoped>
.map--widget {
  position: fixed !important;
  right: 0;
  bottom: 0;
  width: 34%;
  height: calc(100vh - 190px);
}

*, *:focus, *:active {
  outline: none !important;
}

</style>



// WEBPACK FOOTER //
// src/pages/search/parts/search-map.vue